import AddIcon from "@mui/icons-material/Add"
import { Box, Typography } from "@mui/material"
import {
  DataGridPro,
  DataGridProProps,
  GridColDef,
  GridSlotsComponentsProps,
  GridSortModel,
  QuickFilter,
  Toolbar,
  ToolbarButton,
} from "@mui/x-data-grid-pro"
import { CollapsibleTable } from "components/CollapsibleTable/CollapsibleTable"
import { dataGridDEde } from "constants/dataGridLocale"
import { Dayjs } from "dayjs"
import { CustomerOrder } from "generated/graphql"
import { customerOrders } from "queries/customerOrders"
import { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import dayjs from "utils/dayjsConfig"
import { AddOrderDialog } from "./AddOrderDialog"

declare module "@mui/x-data-grid-pro" {
  interface FooterPropsOverrides {
    addClick: () => void
  }
}

export const getEuro = (betrag: number) => {
  return betrag.toFixed(2) + " €"
}

const columnsOrder: GridColDef<CustomerOrderExtended>[] = [
  {
    field: "urorder",
    headerName: "Urorder",
    width: 100,
  },
  {
    field: "datum",
    headerName: "Datum",
    width: 100,
    type: "date",
    valueGetter: (_,row) => {
      if (row.datum === null || row.datum=== undefined) {
        // return 1970 for all invalid dates
        return dayjs("1970-01-01")
      }
      return dayjs(row.datum)
    },
    valueFormatter: (_,row) => {
      return dayjs(row.datum).format("DD.MM.YYYY")
    },
    sortComparator: (v1: Dayjs, v2: Dayjs) => {
      return v1.unix() - v2.unix()
    },
  },
  {
    field: "zieldat",
    headerName: "Zieldatum",
    width: 180,
  },
  {
    field: "status",
    headerName: "Status",
    width: 80,
  },
  {
    field: "zahlart",
    headerName: "Zahlart",
    width: 120,
  },
  {
    field: "rechsum",
    headerName: "Summe",
    width: 120,
    valueGetter: (_,row) =>
      getEuro(row.rechsum ? row.rechsum : 0),
  },
  {
    field: "bezahlt",
    headerName: "Bezahlt",
    width: 100,
    valueGetter: (_, row) =>
      getEuro(row.bezahlt ? row.bezahlt : 0),
  },
  {
    field: "frachtpausch",
    headerName: "Fracht",
    width: 100,
    valueGetter: (_, row) =>
      getEuro(row.frachtpausch ? row.frachtpausch : 0),
  },

  {
    field: "notiz",
    headerName: "Notiz",
    width: 200,
  },
  {
    field: "custnote",
    headerName: "Kundennotiz",
    width: 400,
  },
]

type CustomerEditPageOrderProps = {
  customerID: string
  addOrder: boolean
}

type CustomerOrderExtended = CustomerOrder & {
  hierarchy: string[]
}

const buildHierarchicalCustomerOrders = (
  orders: CustomerOrder[]
): CustomerOrderExtended[] => {
  const res: CustomerOrderExtended[] = []

  // Map orders for fast retrieval
  const orderMap = new Map<number, CustomerOrder>()
  for (let i = 0; i < orders.length; i++) {
    orderMap.set(orders[i].orderID, orders[i])
  }

  // Recursive function to build the hierarchy
  const buildHierarchy = (order: CustomerOrder): string[] => {
    if (order.urorder === 0) {
      // Top-level order, hierarchy is just the order's ID
      return [order.orderID.toString()]
    } else if (order.urorder === order.orderID) {
      //invalid data cycle endless recursion
      return [order.orderID.toString()]
    } else {
      // Recursively get the hierarchy of the parent
      const parentOrder = orderMap.get(order.urorder)
      if (parentOrder) {
        return [...buildHierarchy(parentOrder), order.orderID.toString()]
      } else {
        throw new Error(`Parent order with ID ${order.urorder} not found`)
      }
    }
  }

  // Construct hierarchies for each order
  for (let i = 0; i < orders.length; i++) {
    const order = orders[i]
    const hierarchy = buildHierarchy(order)
    res.push({ ...order, hierarchy })
  }
  return res
}

export const CustomerEditPageOrders = (props: CustomerEditPageOrderProps) => {
  const { customerID, addOrder } = props
  const [isLoading, setIsLoading] = useState(false)
  const [orders, setOrders] = useState<CustomerOrderExtended[]>([])
  const [openAddOrderDialog, setOpenAddOrderDialog] = useState(false)
  const [sortModel, setSortModel] = useState<GridSortModel>([
    {
      field: "datum",
      sort: "desc",
    },
  ])

  const navigate = useNavigate()

  const fetchOrders = () => {
    const id = customerID ? parseInt(customerID) : undefined
    if (id !== undefined) {
      setIsLoading(true)
      customerOrders(id)
        .then((result) => {
          if (result?.customerOrders) {
            setOrders(buildHierarchicalCustomerOrders(result.customerOrders))
          }
        })
        .finally(() => {
          setIsLoading(false)
        })
    }
  }

  useEffect(() => {
    const id = customerID ? parseInt(customerID) : undefined
    if (id !== undefined) {
      setIsLoading(true)
      customerOrders(id)
        .then((result) => {
          if (result?.customerOrders) {
            setOrders(buildHierarchicalCustomerOrders(result.customerOrders))
          }
        })
        .finally(() => {
          setIsLoading(false)
        })
    }
  }, [customerID])

  const getGesamtUmsatz = (): string => {
    if (orders) {
      const sum = orders.reduce((accumulator, currentItem) => {
        return accumulator + currentItem.bezahlt
      }, 0)
      return getEuro(sum)
    }
    return ""
  }

  const CustomToolbar = (
    props: NonNullable<GridSlotsComponentsProps["toolbar"]>
  ) => {
    return (
      <Toolbar>
        <QuickFilter />
        {addOrder && (
          <ToolbarButton onClick={() => setOpenAddOrderDialog(true)}>
            <AddIcon />
            Auftrag anlegen
          </ToolbarButton>
        )}
      </Toolbar>
    )
  }

  const CustomFooterStatusComponent = (
    props: NonNullable<GridSlotsComponentsProps["footer"]>
  ) => {
    return (
      <Toolbar>
        <Typography variant="h6" sx={{ textAlign: "right", flexGrow: 1 }}>
          Gesamtumsatz: {getGesamtUmsatz()}
        </Typography>
      </Toolbar>
    )
  }

  const closeAddOrderDialog = () => {
    setOpenAddOrderDialog(false)
  }

  const closeAddOrderDialogNewOrder = async () => {
    setOpenAddOrderDialog(false)
    fetchOrders()
  }

  const getTreeDataPath: DataGridProProps<CustomerOrderExtended>["getTreeDataPath"] =
    (row) => row.hierarchy

  const groupingColDef: DataGridProProps<CustomerOrderExtended>["groupingColDef"] =
    {
      headerName: "OrderID",
      width: 300,
    }

  return (
    <>
      {orders && (
        <CollapsibleTable
          defaultOpen={true}
          title={`Aufträge (${orders.length})`}
        >
          <Box sx={{ height: 600 }}>
            <DataGridPro
              treeData
              getTreeDataPath={getTreeDataPath}
              groupingColDef={groupingColDef}
              defaultGroupingExpansionDepth={-1}
              rows={orders}
              getRowId={(row) => row.hierarchy[row.hierarchy.length - 1]}
              columns={columnsOrder}
              sortModel={sortModel}
              onSortModelChange={(model) => setSortModel(model)}
              localeText={dataGridDEde}
              rowHeight={60}
              loading={isLoading}
              slots={{
                footer: CustomFooterStatusComponent,
                toolbar: CustomToolbar,
              }}
              slotProps={{
                toolbar: {
                  csvOptions: { disableToolbarButton: true },
                  printOptions: { disableToolbarButton: true },
                  showQuickFilter: true,
                  quickFilterProps: { debounceMs: 100 },
                },
              }}
              onRowClick={(params) => {
                let id = params.id
                if (typeof id === "string") {
                  // on auto-generated rows, the id is a string and contains wrong route params auto-generated-row-null/
                  id = id.replace("auto-generated-row-null/", "")
                }
                navigate(`/orderDetails/${id}`)
              }}
              disableColumnFilter
              disableColumnSelector
              disableDensitySelector
            />
          </Box>
        </CollapsibleTable>
      )}
      <AddOrderDialog
        customerID={customerID}
        open={openAddOrderDialog}
        close={closeAddOrderDialog}
        closeNewOrder={closeAddOrderDialogNewOrder}
      />
    </>
  )
}
